import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch, openAuthenticatedUpload } from '../../api/client';
import {
  OFICIO_STATUS_COLOR,
  OFICIO_STATUS_LABEL,
  formatDateBr,
} from './juridicoUtils';
import JuridicoDistribuicaoPanel from './JuridicoDistribuicaoPanel';



export default function JuridicoOficioDetalhePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [oficio, setOficio] = useState(null);
  const [resumoEdit, setResumoEdit] = useState('');
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [encerrarOpen, setEncerrarOpen] = useState(false);
  const [arquivoFinal, setArquivoFinal] = useState(null);
  const [encerrando, setEncerrando] = useState(false);



  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const res = await apiFetch(`/api/juridico/oficios/${id}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao carregar');
      setOficio(data);
      setResumoEdit(data.resumoIa || '');
    } catch (e) {
      setErro(e.message || 'Erro');
      setOficio(null);
    } finally {
      setLoading(false);
    }
  }, [id]);



  useEffect(() => {
    carregar();
    const t = setInterval(() => {
      setOficio((prev) => {
        if (prev?.status === 'em_analise_ia') carregar();
        return prev;
      });
    }, 4000);
    return () => clearInterval(t);
  }, [carregar]);



  const mergeUpdate = (partial) => {
    if (!partial) {
      carregar();
      return;
    }
    if (partial.id) {
      setOficio((prev) => ({
        ...prev,
        ...partial,
        destinatarios: partial.destinatarios ?? prev?.destinatarios,
        distribuicoes: partial.distribuicoes ?? prev?.distribuicoes,
        historico: partial.historico ?? prev?.historico,
      }));
      if (partial.resumoIa != null) setResumoEdit(partial.resumoIa);
    } else if (partial.destinatarios) {
      setOficio((prev) => ({ ...prev, destinatarios: partial.destinatarios }));
    } else {
      carregar();
    }
  };



  const salvarResumo = async () => {
    setSaving(true);
    setErro('');
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('resumoIa', resumoEdit);
      fd.append('assunto', oficio.assunto);
      fd.append('dataRecebimento', oficio.dataRecebimento);
      const res = await apiFetch(`/api/juridico/oficios/${id}`, { method: 'PUT', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar resumo');
      setOficio((prev) => ({ ...prev, ...data }));
      setMsg('Resumo atualizado.');
    } catch (e) {
      setErro(e.message || 'Falha');
    } finally {
      setSaving(false);
    }
  };



  const reprocessarIa = async () => {
    setMsg('');
    setErro('');
    try {
      const res = await apiFetch(`/api/juridico/oficios/${id}/reprocessar-ia`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro');
      setMsg(data.message || 'IA reiniciada.');
      setTimeout(carregar, 1500);
    } catch (e) {
      setErro(e.message || 'Falha ao reprocessar');
    }
  };



  const encerrar = async () => {
    if (!arquivoFinal) {
      setErro('Selecione a resposta final.');
      return;
    }
    setEncerrando(true);
    setErro('');
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivoFinal);
      const res = await apiFetch(`/api/juridico/oficios/${id}/encerrar`, { method: 'POST', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao encerrar');
      setEncerrarOpen(false);
      setArquivoFinal(null);
      setMsg('Ofício encerrado.');
      await carregar();
    } catch (e) {
      setErro(e.message || 'Falha ao encerrar');
    } finally {
      setEncerrando(false);
    }
  };



  if (loading && !oficio) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }



  if (!oficio) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{erro || 'Ofício não encontrado'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/juridico/oficios')}>
          Voltar
        </Button>
      </Box>
    );
  }



  const st = oficio.statusEfetivo || oficio.status;
  const encerrado = String(oficio.status).toLowerCase() === 'encerrado';
  const isStaff = Boolean(oficio.isJuridicoStaff);
  const podeResponder = Boolean(oficio.podeResponder);



  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/juridico/oficios')} sx={{ textTransform: 'none', mb: 1 }}>
            Lista de ofícios
          </Button>
          <Typography variant="h5" fontWeight={800}>
            {oficio.assunto}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={OFICIO_STATUS_LABEL[st] || st} color={OFICIO_STATUS_COLOR[st] || 'default'} />
            {oficio.numeroOficio && <Chip size="small" variant="outlined" label={`Nº ${oficio.numeroOficio}`} />}
            <Typography variant="body2" color="text.secondary">
              {oficio.orgaoNome || 'Órgão não informado'} · Limite {formatDateBr(oficio.dataLimiteResposta)}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {isStaff && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/juridico/oficios/${id}/editar`)}
                disabled={encerrado}
                sx={{ textTransform: 'none' }}
              >
                Editar
              </Button>
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={reprocessarIa}
                disabled={encerrado}
                sx={{ textTransform: 'none' }}
              >
                Reprocessar IA
              </Button>
              {!encerrado && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<LockIcon />}
                  onClick={() => setEncerrarOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Encerrar ofício
                </Button>
              )}
            </>
          )}
          {podeResponder && (
            <Button
              variant="contained"
              color="success"
              onClick={() => setTab(1)}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              Anexar minha resposta
            </Button>
          )}
        </Stack>
      </Stack>



      {oficio.status === 'em_analise_ia' && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
          Processando análise por IA…
        </Alert>
      )}
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}
      {msg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>
          {msg}
        </Alert>
      )}



      {podeResponder && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setTab(1)} sx={{ fontWeight: 800 }}>
              Ir para resposta
            </Button>
          }
        >
          Você tem pendência de resposta neste ofício. Abra a aba Distribuição / Retorno e anexe seu arquivo.
        </Alert>
      )}

      <Paper sx={{ borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 1 }}>
          <Tab label="Resumo" sx={{ textTransform: 'none', fontWeight: 700 }} />
          <Tab label="Distribuição / Retorno" sx={{ textTransform: 'none', fontWeight: 700 }} />
          <Tab label="Linha do tempo" sx={{ textTransform: 'none', fontWeight: 700 }} />
        </Tabs>
        <Divider />
        <Box sx={{ p: 2.5 }}>
          {tab === 0 && (
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                Recebimento: {formatDateBr(oficio.dataRecebimento)} · Responsável: {oficio.responsavelNome || '—'} ·
                Categoria: {oficio.categoriaNome || '—'}
                {oficio.dataEncerramento ? ` · Encerrado em ${formatDateBr(oficio.dataEncerramento)}` : ''}
              </Typography>
              {oficio.descricao && <Typography variant="body2">{oficio.descricao}</Typography>}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {oficio.arquivoOriginalPath && (
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => openAuthenticatedUpload(oficio.arquivoOriginalPath)}
                    sx={{ textTransform: 'none' }}
                  >
                    Ofício original
                  </Button>
                )}
                {oficio.documentoResumoPath && (
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => openAuthenticatedUpload(oficio.documentoResumoPath)}
                    sx={{ textTransform: 'none' }}
                  >
                    Documento-resumo IA
                  </Button>
                )}
                {oficio.respostaFinalPath && (
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => openAuthenticatedUpload(oficio.respostaFinalPath)}
                    sx={{ textTransform: 'none' }}
                  >
                    Resposta final
                  </Button>
                )}
              </Stack>
              <TextField
                label="Resumo / o que precisa ser feito"
                fullWidth
                multiline
                minRows={5}
                value={resumoEdit}
                onChange={(e) => setResumoEdit(e.target.value)}
                disabled={encerrado || !isStaff}
              />
              {!encerrado && isStaff && (
                <Box>
                  <Button variant="contained" disabled={saving} onClick={salvarResumo} sx={{ textTransform: 'none' }}>
                    {saving ? 'Salvando…' : 'Salvar resumo'}
                  </Button>
                </Box>
              )}
            </Stack>
          )}



          {tab === 1 && (
            <JuridicoDistribuicaoPanel
              oficio={oficio}
              onUpdated={(data) => {
                mergeUpdate(data);
                if (!data?.historico) setTimeout(carregar, 400);
              }}
            />
          )}



          {tab === 2 && (
            <Stack spacing={1.5}>
              {(oficio.historico || []).length === 0 && (
                <Typography color="text.secondary">Sem eventos ainda.</Typography>
              )}
              {(oficio.historico || []).map((h) => (
                <Paper key={h.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {h.dataEvento} · {h.usuarioNome || 'Sistema'} · {h.tipoEvento}
                  </Typography>
                  <Typography variant="body2">{h.descricao}</Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Paper>



      <Dialog open={encerrarOpen} onClose={() => !encerrando && setEncerrarOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Encerrar ofício</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Anexe a resposta final enviada ao órgão. O status passará para <strong>encerrado</strong>.
          </Typography>
          <Button variant="outlined" component="label" sx={{ textTransform: 'none' }}>
            {arquivoFinal ? arquivoFinal.name : 'Selecionar PDF/arquivo'}
            <input
              type="file"
              hidden
              onChange={(e) => setArquivoFinal(e.target.files?.[0] || null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEncerrarOpen(false)} disabled={encerrando} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={encerrando || !arquivoFinal}
            onClick={encerrar}
            sx={{ textTransform: 'none' }}
          >
            {encerrando ? 'Encerrando…' : 'Confirmar encerramento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


